// Color Lookup — applies a 3D LUT (Look Up Table) to the image with
// trilinear interpolation. Supports .cube file parsing and identity LUT
// generation.
//
// The LUT grid is indexed with blue varying outermost and red varying
// innermost, matching the .cube file format specification.

import { PixelBuffer } from "../pixel/PixelBuffer";
import type { Selection } from "../selection/Selection";
import type { Filter } from "./Filter";

export interface Lut3D {
  /** Grid size (e.g. 17 for 17x17x17) */
  size: number;
  /**
   * Flat array of RGB values, length = size^3 * 3.
   * Entry at index (b * size * size + g * size + r) * 3 maps grid position
   * (r, g, b) to output RGB.
   */
  data: Float32Array;
}

export interface ColorLookupParams {
  /** Pre-parsed 3D LUT data. If null, identity is used. */
  lut: Lut3D | null;
  /** Opacity of the LUT effect (0..1). Default 1. */
  opacity: number;
}

const DEFAULT_PARAMS: ColorLookupParams = {
  lut: null,
  opacity: 1,
};

export function createIdentityLut(size: number): Lut3D {
  const data = new Float32Array(size * size * size * 3);
  const step = 1 / (size - 1);
  let idx = 0;
  for (let b = 0; b < size; b++) {
    for (let g = 0; g < size; g++) {
      for (let r = 0; r < size; r++) {
        data[idx++] = r * step * 255;
        data[idx++] = g * step * 255;
        data[idx++] = b * step * 255;
      }
    }
  }
  return { size, data };
}

export function parseCubeLut(cubeText: string): Lut3D | null {
  let size = 0;
  const entries: number[] = [];

  const lines = cubeText.split(/\r?\n/);
  for (const raw of lines) {
    const line = raw.trim();
    if (line.length === 0 || line.startsWith("#")) continue;

    const upper = line.toUpperCase();
    if (upper.startsWith("LUT_3D_SIZE")) {
      const v = parseInt(line.split(/\s+/)[1] ?? "", 10);
      if (isNaN(v) || v < 2) return null;
      size = v;
      continue;
    }

    if (
      upper.startsWith("TITLE") ||
      upper.startsWith("DOMAIN") ||
      upper.startsWith("LUT_1D_SIZE")
    ) {
      continue;
    }

    const parts = line.trim().split(/\s+/);
    if (parts.length >= 3) {
      const r = parseFloat(parts[0] ?? "");
      const g = parseFloat(parts[1] ?? "");
      const b = parseFloat(parts[2] ?? "");
      if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
        entries.push(r, g, b);
      }
    }
  }

  if (size < 2) return null;

  const expected = size * size * size * 3;
  if (entries.length !== expected) return null;

  const data = new Float32Array(expected);
  for (let i = 0; i < expected; i++) {
    data[i] = (entries[i] ?? 0) * 255;
  }

  return { size, data };
}

export const ColorLookup: Filter<ColorLookupParams> = {
  id: "color-lookup",
  displayName: "Color Lookup",
  defaultParams: DEFAULT_PARAMS,

  apply(
    input: PixelBuffer,
    params: ColorLookupParams,
    _selection: Selection | null,
  ): PixelBuffer {
    const w = input.width;
    const h = input.height;
    const out = new PixelBuffer(w, h, input.mode);
    const src = input.data;
    const dst = out.data;
    const n = w * h;

    const lut = params.lut;
    const opacity = clamp01(params.opacity);

    if (lut === null || opacity === 0) {
      dst.set(src);
      return out;
    }

    const size = lut.size;
    const s = size - 1;
    const data = lut.data;

    for (let i = 0; i < n; i++) {
      const o = i * 4;
      const r = src[o] ?? 0;
      const g = src[o + 1] ?? 0;
      const b = src[o + 2] ?? 0;
      const a = src[o + 3] ?? 0;

      const rf = (r / 255) * s;
      const gf = (g / 255) * s;
      const bf = (b / 255) * s;

      const ri = Math.floor(rf);
      const gi = Math.floor(gf);
      const bi = Math.floor(bf);
      const rfrac = rf - ri;
      const gfrac = gf - gi;
      const bfrac = bf - bi;

      const ri1 = Math.min(ri + 1, s);
      const gi1 = Math.min(gi + 1, s);
      const bi1 = Math.min(bi + 1, s);

      const lutIdx = (rr: number, gg: number, bb: number) =>
        (bb * size * size + gg * size + rr) * 3;

      const i000 = lutIdx(ri, gi, bi);
      const i100 = lutIdx(ri1, gi, bi);
      const i010 = lutIdx(ri, gi1, bi);
      const i110 = lutIdx(ri1, gi1, bi);
      const i001 = lutIdx(ri, gi, bi1);
      const i101 = lutIdx(ri1, gi, bi1);
      const i011 = lutIdx(ri, gi1, bi1);
      const i111 = lutIdx(ri1, gi1, bi1);

      const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

      const r00 = lerp(data[i000]!, data[i100]!, rfrac);
      const r10 = lerp(data[i010]!, data[i110]!, rfrac);
      const r01 = lerp(data[i001]!, data[i101]!, rfrac);
      const r11 = lerp(data[i011]!, data[i111]!, rfrac);
      const r0 = lerp(r00, r10, gfrac);
      const r1 = lerp(r01, r11, gfrac);
      let rout = lerp(r0, r1, bfrac);

      const g00 = lerp(data[i000 + 1]!, data[i100 + 1]!, rfrac);
      const g10 = lerp(data[i010 + 1]!, data[i110 + 1]!, rfrac);
      const g01 = lerp(data[i001 + 1]!, data[i101 + 1]!, rfrac);
      const g11 = lerp(data[i011 + 1]!, data[i111 + 1]!, rfrac);
      const g0 = lerp(g00, g10, gfrac);
      const g1 = lerp(g01, g11, gfrac);
      let gout = lerp(g0, g1, bfrac);

      const b00 = lerp(data[i000 + 2]!, data[i100 + 2]!, rfrac);
      const b10 = lerp(data[i010 + 2]!, data[i110 + 2]!, rfrac);
      const b01 = lerp(data[i001 + 2]!, data[i101 + 2]!, rfrac);
      const b11 = lerp(data[i011 + 2]!, data[i111 + 2]!, rfrac);
      const b0 = lerp(b00, b10, gfrac);
      const b1 = lerp(b01, b11, gfrac);
      let bout = lerp(b0, b1, bfrac);

      if (opacity < 1) {
        rout = lerp(r, rout, opacity);
        gout = lerp(g, gout, opacity);
        bout = lerp(b, bout, opacity);
      }

      dst[o] = clampByte(rout);
      dst[o + 1] = clampByte(gout);
      dst[o + 2] = clampByte(bout);
      dst[o + 3] = a;
    }

    return out;
  },
};

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
}
